import { flow, SnapshotIn, toGenerator, types } from 'mobx-state-tree'
import { FetchModel } from '../models/FetchModel'
import { CenterLocationModel } from '../models/common/CenterLocationModel'
import type { FlightNetworkModel } from '../models/FlightNetwork/FlightNetworkModel'
import {
    OperationFailure,
    OperationInProgress,
    OperationNotStarted,
    OperationStatusModel,
    OperationSuccess,
  } from '../models/common/OperationStatus'
import { MeasurementUnitsService } from '../models/MeasurementUnitsService'
import { MeasurementUnitInstances, MeasurementUnitsEnum, MeasurementUnitsModel } from '../models/CenterSettings/MeasurementUnitModel'
import type { DeploymentSettingsModel } from '../models/CenterSettings/DeploymentSettingsModel'
import { UserAndPermissionsModel } from '../models/Users/UserModel'

// A Store for settings used by several pages - like center location, center measurement units etc.
export const CommonSettingsStore = FetchModel.named('CommonSettingsStore')
  .props({
    centerId: types.maybe(types.number),
    centerName: types.maybe(types.string),
    centerLocation: types.maybe(CenterLocationModel),
    centerMeasurementUnits: types.maybe(MeasurementUnitsModel),

    currentUserAndPermissions: types.maybeNull(UserAndPermissionsModel),

    fetchCommonSettingsStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get flightNetworkEndpointUrl(): string {
      return `${self.baseUrl}flightnetworks/get_current/`
    },
    get deploymentSettingsEndpointUrl(): string {
      return `${self.baseUrl}deployment_settings/`
    },
    get getCentersUrl(): string {
      return `${self.baseUrl}centers/`
    },
    get authenticateUrl(): string {
      return `${self.baseUrl}users/authenticate/`
    },
  }))
  .actions((self) => ({
    setMeasurementUnits(measurementUnits: MeasurementUnitsEnum): void {
      self.centerMeasurementUnits = MeasurementUnitInstances[measurementUnits]

      // Important: initialize MeasurementUnitsService
      MeasurementUnitsService.setMeasurementUnits(self.centerMeasurementUnits)
    },
    setCenterName(centerName: typeof self.centerName) {
      self.centerName = centerName
    }
  }))
  .actions((self) => ({
    _loadCenters: flow(function* _loadCenters() {
      return yield* toGenerator(
        self.loadObjectOrError<Array<{id: number, name: string}>>(
          self.getCentersUrl,
          {},
          {fetchOperationName: 'fetch centers'} )
      )
    }),
    _loadMeasurementUnits: flow(function* _loadCenterLocation() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof DeploymentSettingsModel>>(
          self.deploymentSettingsEndpointUrl,
          { }, 
          { fetchOperationName: 'Fetch deployment settings' },
        ),
      )
    }),
    _loadCenterLocation: flow(function* _loadCenterLocation() {
      // We get the center location by fetching the entire flight network, because there's currently no other endpoint for it.
      // Yes, this currently might result in getting the flight network twice. We might add caching later, or a separate endpoint for the center location only. 
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof FlightNetworkModel>>(
          self.flightNetworkEndpointUrl,
          { method: 'POST' }, // Why POST !?!
          { fetchOperationName: 'Fetch flight network' },
        ),
      )
    }),
    _loadUserInfo: flow(function* loadUserInfo() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof UserAndPermissionsModel>>(
          self.authenticateUrl,
          { method: 'POST' },
          { fetchOperationName: 'Authenticate user' },
        ),
      )
    }),
  }))
  .actions((self) => ({
    initializeCommonSettings: flow(function* initializeCommonSettings() {
        if (self.fetchCommonSettingsStatus.isSuccess) {
          // Don't initialize twice
          return
        }

        self.fetchCommonSettingsStatus = OperationInProgress()
  
        const loadCentersResult = yield* toGenerator(self._loadCenters())
        if (loadCentersResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadCentersResult.error)
          return
        }
        const center = loadCentersResult.result[0]
        if (center === undefined) {
            self.fetchCommonSettingsStatus = OperationFailure({name: 'ServerDataError', message: 'GCS must have 1 center location'})
            throw new Error('GCS must have 1 center location')
        }

        const loadCenterLocationResult = yield* toGenerator(self._loadCenterLocation())
        if (loadCenterLocationResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadCenterLocationResult.error)
          return
        }
        const centerLocation = loadCenterLocationResult.result.center_locations?.[0]
        if (centerLocation === undefined) {
            self.fetchCommonSettingsStatus = OperationFailure({name: 'ServerDataError', message: 'Flight network must have at least 1 center location'})
            throw new Error('Flight network must have at least 1 center location')
        }

        const loadMeasurementUnitsResult = yield* toGenerator(self._loadMeasurementUnits())
        if (loadMeasurementUnitsResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadMeasurementUnitsResult.error)
          return
        }

        const loadCurrentUserAndPermissions = yield* toGenerator(self._loadUserInfo())
        if (loadCurrentUserAndPermissions.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadCurrentUserAndPermissions.error)
          return
        }

        self.centerId = center.id
        self.centerName = center.name
        self.setMeasurementUnits(loadMeasurementUnitsResult.result.measurement_units)
        self.centerLocation = CenterLocationModel.create(centerLocation)
        self.currentUserAndPermissions = UserAndPermissionsModel.create(loadCurrentUserAndPermissions.result)

        self.fetchCommonSettingsStatus = OperationSuccess()
    }),
  }))
